/**
 * One rental asset, shared by the Rental Equipment list (AssetRegistry) and an
 * asset's own page (AssetDetailScreen): the shape GET /surface-rentals/assets
 * sends, its status badge, the add / edit form and the body that form writes.
 * The asset used to open in a slide-out panel over the list; it moved to its
 * own page when split panes and drawers beside a list were banned (CLAUDE.md),
 * and both screens need the same reading of the row and the same form.
 */
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { parseMoney } from "../../lib/display-labels";
import { LOAD_FAILURE_QUERY_OPTIONS } from "../../lib/load-failure";

/**
 * A rental asset as GET /surface-rentals/assets sends it: `rental_assets.*`.
 *
 * This page used to read `tag`, `rate`, `yard` / `yard_location`, `mob_fee`,
 * `demob_fee`, `days_out`, `specs` and `current_order`. The table has
 * `asset_number`, `daily_rate`, `current_location` and `specifications`, and
 * since api#953 (0476) `mob_fee` / `demob_fee` (NULL = no fee); it has no
 * days-out figure and no current order (the detail
 * endpoint sends `active_rentals` instead). The screen-side names are kept for
 * display; the add/edit form below writes the column names (#815).
 */
export interface Asset {
  id: string;
  name: string;
  /** `asset_number`. */
  tag: string | null;
  category: string | null;
  status: string | null;
  /** `current_location`. */
  yard: string | null;
  /** `daily_rate`. Null when the asset has no rate. Absent is not $0. */
  rate: number | null;
  /** `mob_fee` / `demob_fee` (api#953). Null is NO fee; 0 is a real $0 fee. */
  mob_fee: number | null;
  demob_fee: number | null;
  serial_number: string | null;
  model: string | null;
  specifications: Record<string, unknown> | null;
  last_pm_date: string | null;
  active_rentals: { order_id?: string; delivered_at?: string | null; applied_rate?: unknown }[] | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

export function normalizeAsset(raw: Record<string, unknown>): Asset {
  const spec = raw.specifications;
  return {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? "—"),
    tag: str(raw.asset_number),
    category: str(raw.category),
    status: str(raw.status),
    yard: str(raw.current_location),
    rate: parseMoney(raw.daily_rate),
    mob_fee: parseMoney(raw.mob_fee),
    demob_fee: parseMoney(raw.demob_fee),
    serial_number: str(raw.serial_number),
    model: str(raw.model),
    specifications: spec && typeof spec === "object" && !Array.isArray(spec) ? (spec as Record<string, unknown>) : null,
    last_pm_date: str(raw.last_pm_date),
    active_rentals: Array.isArray(raw.active_rentals) ? (raw.active_rentals as Asset["active_rentals"]) : null,
  };
}

export type Skin = { label: string; fill: string; rail: string };

/**
 * Exhaustive over the statuses the API writes to `rental_assets.status`:
 * `available` (create, check-in, return), `rented` (delivery and period start),
 * `reserved` (on an upcoming order, api#1091),
 * `maintenance` (work orders, damaged returns) and `retired` (delete). Same
 * hues as the Equipment register. The column has no CHECK, so it can hold other
 * values — this page's own old check-out wrote `deployed` (#815) — and those
 * render a slate dash with a transparent rail, never a real status.
 */
export const STATUS: Record<string, Skin> = {
  available: { label: "Available", fill: "bg-fi-signal-green", rail: "border-l-fi-green" },
  rented: { label: "On Rent", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue" },
  // api#1091: on an upcoming order, not yet delivered. Blue as On Rent (both are
  // committed to an order); the label tells them apart. Not "available".
  reserved: { label: "Reserved", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue" },
  maintenance: { label: "Maintenance", fill: "bg-fi-signal-red", rail: "border-l-fi-red" },
  retired: { label: "Retired", fill: "bg-fi-signal-slate", rail: "border-l-fi-steel" },
};
export const UNKNOWN: Skin = { label: "—", fill: "bg-fi-signal-slate", rail: "border-l-transparent" };
export const skinOf = (s: string | null) =>
  s != null && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : UNKNOWN;

export function StatusBadge({ status }: { status: string | null }) {
  const skin = skinOf(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Blank form helper                                                  */
/* ------------------------------------------------------------------ */

/**
 * The rate is held as TEXT so a blank field is blank: it used to be a number
 * defaulting to 0, which saved an unpriced asset as a $0 one. The mob and
 * demob fees are back (api#953 added the columns), held as text for the same
 * reason: blank is NO fee, a typed 0 is a $0 fee.
 */
export function blankForm() {
  return { tag: "", name: "", category: "", rate: "", yard: "", mob: "", demob: "" };
}

export type AssetForm = ReturnType<typeof blankForm>;

export const formFrom = (a: Asset): AssetForm => ({
  tag: a.tag ?? "",
  name: a.name,
  category: a.category ?? "",
  rate: a.rate == null ? "" : String(a.rate),
  yard: a.yard ?? "",
  mob: a.mob_fee == null ? "" : String(a.mob_fee),
  demob: a.demob_fee == null ? "" : String(a.demob_fee),
});

/**
 * The form as `POST` / `PUT /surface-rentals/assets` read it (#815): the
 * columns are `asset_number`, `daily_rate`, `current_location`, `mob_fee` and
 * `demob_fee`. Status is not sent: the API defaults a new asset to `available`.
 *
 * BLANK MEANS ABSENT, NEVER 0. On create a blank field is omitted. On edit a
 * field that HAD a value and is now blank is sent as `null`, which CLEARS it
 * (api#973: absent keeps, a
 * value sets, null clears). It used to be refused here because the PUT kept
 * the old value whatever was sent; a field the API will not clear answers 400
 * FIELD_NOT_CLEARABLE, shown as sent.
 */
export function assetBody(form: AssetForm, original: Asset | null): { body: Record<string, unknown> } | { error: string } {
  const body: Record<string, unknown> = {
    asset_number: form.tag.trim(),
    name: form.name.trim(),
    category: form.category,
  };
  // On edit, a field that had a value and is now blank is cleared; one that was
  // already empty is left out rather than "cleared" again.
  const had: Record<string, unknown> = original
    ? { daily_rate: original.rate, current_location: original.yard, mob_fee: original.mob_fee, demob_fee: original.demob_fee }
    : {};
  const blank = (key: string) => {
    if (had[key] != null) body[key] = null;
  };
  const rateText = form.rate.trim();
  if (rateText === "") blank("daily_rate");
  else {
    const rate = Number(rateText);
    // A typed 0 is a real $0 day rate and bills as $0 (api#1063); only blank
    // means "no rate". A negative rate is still no rate to the resolver.
    if (!Number.isFinite(rate) || rate < 0) return { error: "Day rate can't be negative. Leave it blank for no rate." };
    body.daily_rate = rate;
  }
  if (form.yard.trim() === "") blank("current_location");
  else body.current_location = form.yard.trim();
  // A fee of 0 is a real $0 fee; the API checks the amount (two decimals, not
  // negative) and its refusal is shown as sent.
  for (const [key, text, label] of [["mob_fee", form.mob, "Mob fee"], ["demob_fee", form.demob, "Demob fee"]] as const) {
    const t = text.trim();
    if (t === "") blank(key);
    else {
      const n = Number(t);
      if (!Number.isFinite(n)) return { error: `${label} must be an amount, or left blank for no fee.` };
      body[key] = n;
    }
  }
  return { body };
}

/* ------------------------------------------------------------------ */
/*  Reads                                                              */
/* ------------------------------------------------------------------ */

/** The list's key. An asset's own read sits under it, so invalidating the list refreshes both. */
export const RENTAL_ASSETS_KEY = ["rentals", "assets"] as const;

/** GET /surface-rentals/assets — unpaginated, every asset the tenant has. */
export function useRentalAssets() {
  return useQuery({
    queryKey: RENTAL_ASSETS_KEY,
    queryFn: () =>
      api.rentals.assets.list().then((r) => unwrapArray<Record<string, unknown>>(r.data).map(normalizeAsset)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * The tenant's categories, plus any category an asset already carries. This
 * used to fall back to nine hard-coded category names whenever the tenant had
 * none, which offered filters for equipment nobody owned.
 */
export function useAssetCategoryNames(assets: Asset[] | undefined): string[] {
  const { data: apiCategories = [] } = useQuery({
    queryKey: ["rentals", "asset-categories"],
    queryFn: () =>
      api.rentals.assetCategories.list().then((r) => {
        const d = r.data?.data ?? r.data ?? [];
        return Array.isArray(d) ? d : [];
      }),
  });
  return useMemo(() => {
    const configured = apiCategories
      .filter((c: any) => c.active !== false)
      .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map((c: any) => String(c.name));
    const inUse = (assets ?? []).map((a) => a.category).filter((c): c is string => !!c);
    return Array.from(new Set([...configured, ...inUse]));
  }, [apiCategories, assets]);
}

/* ------------------------------------------------------------------ */
/*  The add / edit form                                                */
/* ------------------------------------------------------------------ */

/**
 * The asset form's fields. The category picker can create a category in place;
 * "Manage categories" is the caller's dialog, so it is not stacked inside this
 * form's own dialog.
 */
export function AssetFormFields({
  form,
  setForm,
  idPrefix,
  categoryNames,
  onManageCategories,
}: {
  form: AssetForm;
  setForm: (fn: (prev: AssetForm) => AssetForm) => void;
  idPrefix: string;
  categoryNames: string[];
  onManageCategories: () => void;
}) {
  const queryClient = useQueryClient();
  const [newCategoryText, setNewCategoryText] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) => api.rentals.assetCategories.create({ name }),
    onSuccess: (_data, name) => {
      queryClient.invalidateQueries({ queryKey: ["rentals", "asset-categories"] });
      toast.success(`Category "${name}" created`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || "Failed to create category"),
  });

  const handleInlineCreateCategory = async (name: string) => {
    if (!name.trim()) return;
    setCreatingCategory(true);
    try {
      await createCategoryMutation.mutateAsync(name.trim());
      setForm((p) => ({ ...p, category: name.trim() }));
      setNewCategoryText("");
    } finally {
      setCreatingCategory(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`${idPrefix}-number`}>Asset Number</Label>
        <Input
          id={`${idPrefix}-number`}
          value={form.tag}
          onChange={(e) => setForm((p) => ({ ...p, tag: e.target.value }))}
          placeholder="#XX-0000"
          className="h-9"
        />
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-name`}>Name</Label>
        <Input
          id={`${idPrefix}-name`}
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          placeholder="Asset name"
          className="h-9"
        />
      </div>
      <div>
        <Label>Category</Label>
        <Select value={form.category} onValueChange={(v) => {
          if (v === '__create__') return;
          if (v === '__manage__') { onManageCategories(); return; }
          setForm((p) => ({ ...p, category: v }));
        }}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categoryNames.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
            <div className="border-t border-fieldiq-border mt-1 pt-1">
              <div className="px-3 py-1.5">
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={newCategoryText}
                    onChange={e => setNewCategoryText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && newCategoryText.trim()) { e.preventDefault(); handleInlineCreateCategory(newCategoryText); } }}
                    placeholder="New category name..."
                    className="flex-1 bg-fieldiq-dark border border-fieldiq-border rounded px-2 py-1 text-sm text-fieldiq-text placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50"
                    onClick={e => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleInlineCreateCategory(newCategoryText); }}
                    disabled={!newCategoryText.trim() || creatingCategory}
                    className="px-2 py-1 text-xs font-semibold text-fieldiq-accent-signal hover:text-fieldiq-text disabled:opacity-50 flex items-center gap-1"
                  >
                    {creatingCategory ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Create
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onManageCategories()}
                className="w-full text-left px-3 py-1.5 text-xs text-fieldiq-accent-signal hover:text-fieldiq-text hover:bg-fieldiq-border/50 transition-colors"
              >
                Manage categories &rarr;
              </button>
            </div>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-rate`}>Day Rate ($)</Label>
        <Input
          id={`${idPrefix}-rate`}
          type="number"
          inputMode="decimal"
          value={form.rate}
          onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))}
          placeholder="No rate"
          className="h-9"
        />
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-yard`}>Yard Location</Label>
        <Input
          id={`${idPrefix}-yard`}
          value={form.yard}
          onChange={(e) => setForm((p) => ({ ...p, yard: e.target.value }))}
          placeholder="Bay / Row"
          className="h-9"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`${idPrefix}-mob`}>Mob Fee ($)</Label>
          <Input
            id={`${idPrefix}-mob`}
            type="number"
            inputMode="decimal"
            value={form.mob}
            onChange={(e) => setForm((p) => ({ ...p, mob: e.target.value }))}
            placeholder="No fee"
            className="h-9"
          />
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-demob`}>Demob Fee ($)</Label>
          <Input
            id={`${idPrefix}-demob`}
            type="number"
            inputMode="decimal"
            value={form.demob}
            onChange={(e) => setForm((p) => ({ ...p, demob: e.target.value }))}
            placeholder="No fee"
            className="h-9"
          />
        </div>
      </div>
      <p className="text-xs text-fieldiq-muted">Blank is no fee; 0 is a $0 fee.</p>
    </div>
  );
}
